/**
 * Google reCAPTCHA v3 utility functions
 */

const RECAPTCHA_SITE_KEY = '6Le7yjMsAAAAAO7tzdfIq0_j9Sy_hSTgg-I-4abj';

// Track if reCAPTCHA is ready
let recaptchaReady = false;

/**
 * Initialize reCAPTCHA (call this on page load)
 */
export const initRecaptcha = () => {
  if (typeof window !== 'undefined' && window.grecaptcha) {
    window.grecaptcha.ready(() => {
      recaptchaReady = true;
    });
  }
};

/**
 * Wait for reCAPTCHA to be ready
 * @param {number} maxWait - Maximum wait time in ms (default: 5000)
 * @returns {Promise<void>}
 */
const waitForRecaptcha = (maxWait = 5000) => {
  return new Promise((resolve, reject) => {
    if (typeof window === 'undefined' || !window.grecaptcha) {
      reject(new Error('reCAPTCHA script not loaded'));
      return;
    }

    if (recaptchaReady) {
      resolve();
      return;
    }

    const startTime = Date.now();
    const checkInterval = setInterval(() => {
      if (recaptchaReady || (typeof window !== 'undefined' && window.grecaptcha)) {
        clearInterval(checkInterval);
        recaptchaReady = true;
        resolve();
      } else if (Date.now() - startTime > maxWait) {
        clearInterval(checkInterval);
        reject(new Error('reCAPTCHA initialization timeout'));
      }
    }, 100);
  });
};

/**
 * Execute reCAPTCHA v3 and get token
 * @param {string} action - Action name (e.g., 'login', 'password_reset', 'otp_send')
 * @param {number} retries - Number of retry attempts (default: 0)
 * @returns {Promise<string>} - reCAPTCHA token
 */
export const executeRecaptcha = async (action = 'submit', retries = 0) => {
  try {
    // Wait for reCAPTCHA to be ready
    await waitForRecaptcha();
  } catch (error) {
    if (retries > 0) {
      // Wait a bit and retry
      await new Promise(resolve => setTimeout(resolve, 500));
      return executeRecaptcha(action, retries - 1);
    }
    throw new Error('reCAPTCHA script not loaded. Please refresh the page.');
  }

  return new Promise((resolve, reject) => {
    window.grecaptcha.ready(() => {
      window.grecaptcha
        .execute(RECAPTCHA_SITE_KEY, { action })
        .then((token) => {
          if (!token) {
            throw new Error('Empty token received');
          }
          resolve(token);
        })
        .catch((error) => {
          console.error('reCAPTCHA execution error:', error);
          // Retry once if we have retries left
          if (retries > 0) {
            setTimeout(() => {
              executeRecaptcha(action, retries - 1).then(resolve).catch(reject);
            }, 300);
            return;
          }
          reject(new Error('Failed to execute reCAPTCHA. Please try again.'));
        });
    });
  });
};

/**
 * Check if reCAPTCHA is loaded
 * @returns {boolean}
 */
export const isRecaptchaLoaded = () => {
  return typeof window !== 'undefined' && typeof window.grecaptcha !== 'undefined';
};

